import {Component, computed, inject, signal} from '@angular/core';
import {technicalConclusionResourceType} from "./config/technical-conclusion-config";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {
  TechnicalConclusion,
  TechnicalConclusionDialogData,
  TechnicalConclusionFormField
} from "./types/technical-conclusion-types";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {MatButton, MatMiniFabButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {FormGroup} from "@angular/forms";
import {GetDataInModalParams} from "../../../types/product-card-modals.types";
import {TechnicalConclusionModalService} from "./services/technical-conclusion-modal.service";
import {TechnicalConclusionFilesStateService} from "./services/technical-conclusion-files-state.service";
import {technicalConclusionFormFields} from "./config/technical-conclusion-form-fields";
import {defaultTechnicalConclusionData} from "./config/default-technical-conclusion-data";
import {ProductCardFormFieldService} from "../../../services/product-card-form-field.service";
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {LoadingService} from "../../../../../../core/services/loading.service";
import {EditModeService} from "../../../../../../core/services/edit-mode.service";
import {
  TechnicalConclusionFormComponent
} from "./components/technical-conclusion-form/technical-conclusion-form.component";
import {
  TechnicalConclusionFilesComponent
} from "./components/technical-conclusion-files/technical-conclusion-files.component";

@Component({
  selector: 'app-technical-conclusion-modal',
  imports: [
    BaseModalComponent,
    MatAccordion,
    MatButton,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatIcon,
    MatMiniFabButton,
    TechnicalConclusionFormComponent,
    TechnicalConclusionFilesComponent
  ],
  templateUrl: './technical-conclusion-modal.component.html',
  styleUrl: './technical-conclusion-modal.component.scss',
  providers: [ LoadingService, EditModeService ]
})
export class TechnicalConclusionModalComponent extends BaseModalComponent {
  private technicalConclusionDialogData = inject<TechnicalConclusionDialogData>(MAT_DIALOG_DATA);
  private technicalConclusionModalService: TechnicalConclusionModalService = inject(TechnicalConclusionModalService);
  private technicalConclusionFilesStateService: TechnicalConclusionFilesStateService = inject(TechnicalConclusionFilesStateService);
  private productCardFormFieldService: ProductCardFormFieldService = inject(ProductCardFormFieldService);

  public readonly technicalConclusionResourceType = technicalConclusionResourceType;
  public technicalConclusionsData = signal<Array<TechnicalConclusion>>([]);
  public technicalConclusionFormGroups = signal<Array<FormGroup>>([]);
  public isValid = signal<boolean>(false);
  public noFirstData = signal<boolean>(false);
  public technicalParametersId = computed(() => Number(this.technicalConclusionDialogData.technicalConclusions.id));
  public parentId = computed(() => this.technicalConclusionDialogData.parentId);

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initModal();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(this.technicalConclusionResourceType.key);
  }

  public handleAddNew(): void {
    const formGroup = this.baseModalService.getFormGroup<TechnicalConclusionFormField>(technicalConclusionFormFields);
    this.technicalConclusionFormGroups.update(forms  => [ ...forms, formGroup ]);
    this.technicalConclusionsData.update(data  => [ ...data, defaultTechnicalConclusionData ]);
    this.isValid.set(false);
  }

  public updateTechnicalConclusionData(data: TechnicalConclusion, index: number): void {
    this.technicalConclusionsData()[index] = data;
    this.technicalConclusionFormGroups().at(index)?.patchValue(data);
    this.isValid.set(!!data.id);
    this.noFirstData.set(!!this.technicalConclusionsData().length);
  }

  public deleteTechnicalConclusion(index: number): void {
    this.technicalConclusionsData.update(data => {
      const newData = [...data];
      newData.splice(index, 1);
      return newData;
    });

    this.technicalConclusionFormGroups.update(groups => {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      return newGroups;
    });

    this.isValid.set(true);
  }

  public async handleDelete(event: MouseEvent, id: number, index: number): Promise<void> {
    event.stopPropagation();
    const isConfirmed = await this.productCardFormFieldService.isDeleteConfirmed(id);
    if (!isConfirmed) { return; }

    this.loadingService.setIsLoadingState(true);
    await this.deleteRecord(technicalConclusionResourceType.path, id)
      .then(() => this.removeTechnicalConclusionAtIndex(index, id))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private async initModal(): Promise<void> {
    this.modalStatesService.openModal(this.technicalConclusionResourceType.key);
    this.loadingService.setIsLoadingState(true);
    await this.loadTechnicalConclusionData();
  }

  private async loadTechnicalConclusionData(): Promise<void> {
    const params: GetDataInModalParams = {
      path: technicalConclusionResourceType.path,
      queryParams: {
        technicalParametersId: this.technicalParametersId()
      }
    };

    await this.technicalConclusionModalService.getDataInModal<TechnicalConclusion>(params)
      .then(data => this.successLoad(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private successLoad(data: Array<TechnicalConclusion>): void {
    this.technicalConclusionsData.set(data);
    this.isValid.set(!!this.technicalConclusionsData()?.at(0)?.id);
    this.noFirstData.set(!!this.technicalConclusionsData()?.at(0)?.id);
    this.initForm();
  }

  private initForm(): void {
    if (!this.technicalConclusionsData().length) {
      this.handleAddNew();
      return;
    }

    const formGroups = this.technicalConclusionsData().map(data => {
      const formGroup = this.baseModalService.getFormGroup<TechnicalConclusionFormField>(technicalConclusionFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });

    this.technicalConclusionFormGroups.set(formGroups);
  }

  private removeTechnicalConclusionAtIndex(index: number, id: number): void {
    this.technicalConclusionFormGroups.update(groups => {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      return newGroups;
    });

    this.technicalConclusionsData.update(data => {
      const newData = [...data];
      newData.splice(index, 1);
      return newData;
    });

    this.technicalConclusionFilesStateService.deleteTechnicalConclusionFilesState(id);
    this.isValid.set(!!this.technicalConclusionsData().length);
    if (!this.technicalConclusionsData().length) {
      this.noFirstData.set(false);
      this.handleAddNew();
    }
  }
}
